<template>
    <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-primary-200">
        <!-- Loading State -->
        <div v-if="loading" class="tw-animate-pulse">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-48 tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36"></div>
                </div>
                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-32"></div>
            </div>
            
            <div class="tw-max-w-4xl">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <div class="tw-space-y-6">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-40 tw-mb-4"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                        <div class="tw-h-32 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                    </div>
                    <div class="tw-space-y-6">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-4"></div>
                        <div class="tw-h-64 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div v-else>
            <!-- Header Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Edit Service</h1>
                    <p class="tw-text-sm tw-text-gray-600">Update information for "{{ originalService?.name || 'Loading...' }}"</p>
                </div>
                
                <!-- Back Button -->
                <a-button
                    @click="goBack"
                    intent="light"
                    left-icon="solar:arrow-left-linear"
                >
                    Back to Services
                </a-button>
            </div>

            <!-- Form Content -->
            <form @submit.prevent="updateService" class="tw-max-w-4xl">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <!-- Left Column - Service Information -->
                    <div class="tw-space-y-6">
                        <div>
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Service Information</h2>
                            
                            <!-- Service Name -->
                            <div class="tw-mb-6">
                                <FormField
                                    v-model="serviceForm.name"
                                    type="text"
                                    label="Service Name"
                                    placeholder="Enter service name"
                                    left-icon="solar:settings-linear"
                                    :error="serviceErrors.name"
                                    required
                                    help="Enter a clear, descriptive name for your service"
                                />
                            </div>

                            <!-- Service Description -->
                            <div class="tw-mb-6">
                                <FormField
                                    v-model="serviceForm.description"
                                    type="textarea"
                                    label="Description"
                                    placeholder="Enter detailed service description"
                                    :error="serviceErrors.description"
                                    rows="6"
                                    help="Provide a detailed description of your service features and benefits"
                                />
                            </div>

                            <!-- Categories -->
                            <div class="tw-mb-6">
                                <FormMultiSelect
                                    v-model="serviceForm.categories"
                                    :options="categoryOptions"
                                    label="Categories"
                                    placeholder="Select service categories"
                                    left-icon="solar:widget-bold-duotone"
                                    :error="serviceErrors.categories"
                                    :loading="categoriesLoading"
                                    help="Choose relevant categories to help customers find your service"
                                />
                            </div>

                            <!-- Status Toggle -->
                            <div class="tw-mb-6">
                                <div class="tw-space-y-2">
                                    <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                                        <icon icon="solar:settings-linear" class="tw-text-base" />
                                        Status
                                    </label>
                                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white">
                                        <!-- Active Button -->
                                        <button
                                            type="button"
                                            @click="serviceForm.status = 'active'"
                                            :class="[
                                                'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-flex-1 tw-justify-center',
                                                serviceForm.status === 'active' 
                                                    ? 'tw-bg-green-500 tw-text-white tw-shadow-sm' 
                                                    : 'tw-text-gray-600 hover:tw-bg-gray-50'
                                            ]"
                                        >
                                            <icon 
                                                :icon="serviceForm.status === 'active' ? 'solar:check-circle-bold' : 'solar:check-circle-linear'" 
                                                class="tw-text-base" 
                                            />
                                            Active
                                        </button>
                                        
                                        <!-- Inactive Button -->
                                        <button
                                            type="button"
                                            @click="serviceForm.status = 'inactive'"
                                            :class="[
                                                'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-flex-1 tw-justify-center',
                                                serviceForm.status === 'inactive' 
                                                    ? 'tw-bg-red-500 tw-text-white tw-shadow-sm' 
                                                    : 'tw-text-gray-600 hover:tw-bg-gray-50'
                                            ]"
                                        >
                                            <icon 
                                                :icon="serviceForm.status === 'inactive' ? 'solar:close-circle-bold' : 'solar:close-circle-linear'" 
                                                class="tw-text-base" 
                                            />
                                            Inactive
                                        </button>
                                    </div>
                                    <p class="tw-text-xs tw-text-gray-500">
                                        Active services are visible to customers and can be booked
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Service Image -->
                    <div class="tw-space-y-6">
                        <div>
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Service Image</h2>
                            
                            <!-- Image Upload Area -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Service Image <span class="tw-text-gray-500">(Optional)</span>
                                </label>
                                
                                <div
                                    @drop.prevent="handleImageDrop"
                                    @dragover.prevent="isDragging = true"
                                    @dragleave.prevent="isDragging = false"
                                    @click="triggerImageInput"
                                    :class="[
                                        'tw-relative tw-w-full tw-h-64 tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                                        isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400',
                                        serviceForm.imagePreview ? 'tw-border-solid' : ''
                                    ]"
                                >
                                    <!-- Image Preview -->
                                    <div v-if="serviceForm.imagePreview" class="tw-relative tw-w-full tw-h-full">
                                        <img
                                            :src="serviceForm.imagePreview"
                                            alt="Service preview"
                                            class="tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                                        />
                                        <button
                                            @click.stop="removeImage"
                                            type="button"
                                            class="tw-absolute tw-top-2 tw-right-2 tw-w-8 tw-h-8 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors"
                                        >
                                            <icon icon="solar:close-circle-bold" class="tw-text-lg" />
                                        </button>
                                    </div>
                                    
                                    <!-- Upload Placeholder -->
                                    <div v-else class="tw-text-center">
                                        <icon icon="solar:camera-add-bold-duotone" class="tw-text-6xl tw-text-gray-400 tw-mb-4 tw-mx-auto" />
                                        <p class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-2">
                                            Drop an image here or click to upload
                                        </p>
                                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4">
                                            Supports JPG, PNG, GIF files up to 5MB
                                        </p>
                                        <div class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-primary-100 tw-text-primary-700 tw-rounded-md tw-text-sm tw-font-medium">
                                            <icon icon="solar:upload-bold-duotone" class="tw-mr-2" />
                                            Choose Image
                                        </div>
                                    </div>
                                    
                                    <input
                                        ref="imageInput"
                                        type="file"
                                        accept="image/*"
                                        @change="handleImageSelect"
                                        class="tw-hidden"
                                    />
                                </div>
                                
                                <p class="tw-text-xs tw-text-gray-500 tw-mt-2">
                                    Recommended size: 800x600px. Maximum file size: 5MB
                                </p>
                            </div>

                            <!-- Image Upload Tips -->
                            <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2">
                                    <icon icon="solar:info-circle-bold" class="tw-mr-1" />
                                    Image Tips
                                </h3>
                                <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                    <li>• Use high-quality images for better presentation</li>
                                    <li>• Square or landscape orientation works best</li>
                                    <li>• Ensure good lighting and clear service representation</li>
                                    <li>• Avoid images with heavy text overlays</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pt-8 tw-mt-8 tw-border-t tw-border-gray-200">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Delete Button -->
                        <a-button
                            type="button"
                            intent="danger"
                            @click="deleteService"
                            :disabled="savingService"
                            left-icon="solar:trash-bin-2-bold-duotone"
                        >
                            Delete Service
                        </a-button>
                    </div>

                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Cancel Button -->
                        <a-button
                            type="button"
                            intent="white"
                            @click="goBack"
                            :disabled="savingService"
                        >
                            Cancel
                        </a-button>
                        
                        <!-- Update Service Button -->
                        <a-button
                            type="submit"
                            intent="primary"
                            :loading="savingService"
                            :disabled="!serviceForm.name || savingService"
                            left-icon="solar:pen-bold-duotone"
                            class="tw-min-w-[180px]"
                        >
                            Update Service
                        </a-button>
                    </div>
                </div>
            </form>
        </div>

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingService"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            title="Delete Service?"
            description="This action cannot be undone. The service will be permanently deleted."
        />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import FormField from '@/components/form/FormField.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import useAlert from '@/composables/useAlert'
// API imports
import { get as getService, update as updateServiceApi, destroy as destroyService } from '@/api/services'
import appApi from '@/api/app'

const router = useRouter()
const route = useRoute()

// Form state
const loading = ref(true)
const savingService = ref(false)
const deleteModalVisible = ref(false)
const deletingService = ref(false)
const originalService = ref(null)
const isDragging = ref(false)
const imageInput = ref(null)
const categoriesLoading = ref(false)
const categories = ref([])

// Service form
const serviceForm = reactive({
    name: '',
    description: '',
    image: null,
    imagePreview: '',
    categories: [],
    status: 'active'
})

const serviceErrors = reactive({
    name: '',
    description: '',
    categories: '',
    status: ''
})

// API Methods
const fetchCategories = async () => {
    categoriesLoading.value = true
    
    try {
        const response = await appApi.forms.categories('service')
        categories.value = response.data || []
    } catch (error) {
        console.error('Error fetching categories:', error)
        categories.value = []
        
        // Handle different error types
        if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else {
            useAlert('Failed to load service categories. Please check your connection.', 'warning')
        }
    } finally {
        categoriesLoading.value = false
    }
}

// Image handling methods
const triggerImageInput = () => {
    imageInput.value?.click()
}

const handleImageSelect = (event) => {
    const file = event.target.files?.[0]
    if (file) {
        processImage(file)
    }
}

const handleImageDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer?.files?.[0]
    if (file) {
        processImage(file)
    }
}

const processImage = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
        useAlert('Please select a valid image file', 'danger')
        return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
        useAlert('Image size must be less than 5MB', 'danger')
        return
    }

    serviceForm.image = file

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        serviceForm.imagePreview = e.target?.result
    }
    reader.readAsDataURL(file)
}

const removeImage = () => {
    serviceForm.image = null
    serviceForm.imagePreview = ''
    if (imageInput.value) {
        imageInput.value.value = ''
    }
}

// Computed
const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

// Methods
const goBack = () => {
    router.push({ name: 'pages.company.services' })
}

const loadService = async () => {
    loading.value = true
    
    try {
        const serviceId = route.params.id
        const response = await getService(serviceId)
        
        if (response.data) {
            const service = response.data
            
            // Store original service data
            originalService.value = service

            // Populate form
            serviceForm.name = service.name || ''
            serviceForm.description = service.description || ''
            serviceForm.imagePreview = service.image || ''
            serviceForm.categories = service.categories || []
            serviceForm.status = service.status || 'active'
        } else {
            throw new Error('Service not found')
        }

    } catch (error) {
        console.error('Error loading service:', error)
        
        // Handle different error types
        if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to edit this service.', 'danger')
        } else if (error.response?.status === 404) {
            useAlert('Service not found.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else {
            useAlert('Error loading service. Please try again.', 'danger')
        }
        
        goBack()
    } finally {
        loading.value = false
    }
}

const validateServiceForm = () => {
    // Reset errors
    Object.keys(serviceErrors).forEach(key => {
        serviceErrors[key] = ''
    })

    let isValid = true

    // Name validation
    if (!serviceForm.name.trim()) {
        serviceErrors.name = 'Service name is required'
        isValid = false
    } else if (serviceForm.name.trim().length < 3) {
        serviceErrors.name = 'Service name must be at least 3 characters'
        isValid = false
    } else if (serviceForm.name.trim().length > 100) {
        serviceErrors.name = 'Service name must be less than 100 characters'
        isValid = false
    }

    // Description validation
    if (serviceForm.description.trim().length > 1000) {
        serviceErrors.description = 'Description must be less than 1000 characters'
        isValid = false
    }

    return isValid
}

const updateService = async () => {
    if (!validateServiceForm()) {
        useAlert('Please fix the form errors', 'warning')
        return
    }

    savingService.value = true

    try {
        const serviceId = route.params.id
        
        // Create FormData for file upload
        const formData = new FormData()
        
        // Add required fields
        formData.append('name', serviceForm.name.trim())
        formData.append('description', serviceForm.description.trim())
        formData.append('status', serviceForm.status)
        
        // Add update flag for image
        formData.append('is_image_updated', serviceForm.image ? 'true' : 'false')
        
        // Add image if a new one was selected
        if (serviceForm.image) {
            formData.append('image', serviceForm.image)
        }
        
        // Add categories as JSON array
        if (serviceForm.categories && serviceForm.categories.length > 0) {
            const categoryIds = serviceForm.categories.map(cat => cat.id)
            formData.append('categories', JSON.stringify(categoryIds))
        }



        // Call the API
        const response = await updateServiceApi(serviceId, formData)
        
        if (response.status === 200 || response.status === 201) {
            // Show success message
            const successMessage = response.data?.message || 'Service updated successfully!'
            useAlert(successMessage, 'success')
            
            // Navigate back to services list
            router.push({ name: 'pages.company.services' })
        } else {
            // Handle API response errors
            const message = response.data?.message || 'Failed to update service'
            useAlert(message, 'danger')
        }

    } catch (error) {
        console.error('Error updating service:', error)
        
        // Handle different types of errors
        if (error.response?.status === 422) {
            // Validation errors
            const errors = error.response.data?.errors || {}
            
            // Map backend errors to form errors
            Object.keys(errors).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(serviceErrors, key)) {
                    serviceErrors[key] = errors[key][0] || errors[key]
                }
            })
            
            useAlert('Please correct the form errors', 'warning')
        } else if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to update this service.', 'danger')
        } else if (error.response?.status === 404) {
            useAlert('Service not found.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
            useAlert('Network error. Please check your connection and try again.', 'danger')
        } else {
            const message = error.response?.data?.message || 'Error updating service. Please try again.'
            useAlert(message, 'danger')
        }
    } finally {
        savingService.value = false
    }
}

const deleteService = () => {
    deleteModalVisible.value = true
}

const confirmDelete = async () => {
    deletingService.value = true
    
    try {
        const serviceId = route.params.id
        
        // Call the API to delete the service
        const response = await destroyService(serviceId)
        
        if (response.status === 200 || response.status === 204) {
            // Show success message
            const successMessage = response.data?.message || 'Service deleted successfully!'
            useAlert(successMessage, 'success')
            
            // Navigate back to services list
            router.push({ name: 'pages.company.services' })
        } else {
            // Handle API response errors
            const message = response.data?.message || 'Failed to delete service'
            useAlert(message, 'danger')
        }
        
    } catch (error) {
        console.error('Error deleting service:', error)
        
        // Handle different types of errors
        if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to delete this service.', 'danger')
        } else if (error.response?.status === 404) {
            useAlert('Service not found.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
            useAlert('Network error. Please check your connection and try again.', 'danger')
        } else {
            const message = error.response?.data?.message || 'Error deleting service. Please try again.'
            useAlert(message, 'danger')
        }
    } finally {
        deletingService.value = false
        deleteModalVisible.value = false
    }
}

// Initialize
onMounted(async () => {
    // Load categories and service data in parallel
    await Promise.all([
        fetchCategories(),
        loadService()
    ])
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 